import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  MenuItem,
  CircularProgress,
  Alert,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import * as quoteApi from '../../api/quotes';
import * as clientApi from '../../api/clients';
import * as propertyApi from '../../api/properties';
import { CreateQuoteDTO, SupplierRate } from '../../types/quote.types';
import { Client } from '../../types/client';
import { Property, PropertyAddress } from '../../types/property.types';
import { Contact } from '../../types/contact.types';
import { getClientContacts } from '../../api/contacts';
import { validateQuoteCreation } from '../../utils/validation';
import SupplierDropdown from '../../components/Quote/SupplierDropdown';



interface FormData {
  clientId: string;
  propertyId?: string;
  // Audit 07 R-5: deliberate send-target — which Contact the quote is
  // addressed to. Blank = role-based default (active PRIMARY).
  contactId?: string;
  meterType: 'MPAN' | 'MPRN';
  meterIdentifier: string;
  // Energy consumption fields
  eac?: number; // Estimated Annual Consumption for Electric (kWh)
  aq?: number;  // Annual Quantity for Gas (kWh)
  // Internal fields
  commissionUplift?: number; // Range: 0.0 to 5.0, 1 decimal place
  currentSupplier: {
    supplier: string; // Supplier ID
    supplierName: string; // Cached supplier name
    standingCharge?: number;
    dayUnitRate?: number;
    nightUnitRate?: number;
    eveningWeekendRate?: number;
    annualConsumption: {
      day: number;
      night?: number;
      eveningWeekend?: number;
    };
    annualExpenditure: number;
    monthlyExpenditure: number;
  };
}

const initialFormData: FormData = {
  clientId: '',
  propertyId: '',
  contactId: '',
  meterType: 'MPAN',
  meterIdentifier: '',
  eac: undefined,
  aq: undefined,
  commissionUplift: undefined,
  currentSupplier: {
    supplier: '',
    supplierName: '',
    standingCharge: undefined,
    dayUnitRate: undefined,
    nightUnitRate: undefined,
    eveningWeekendRate: undefined,
    annualConsumption: {
      day: 0,
      night: undefined,
      eveningWeekend: undefined,
    },
    annualExpenditure: 0,
    monthlyExpenditure: 0,
  },
};

const QuoteForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const clientIdFromUrl = queryParams.get('clientId');
  const propertyIdFromUrl = queryParams.get('propertyId');
  const { id: quoteIdFromUrl } = useParams<{ id: string }>();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [supplierRates, setSupplierRates] = useState<SupplierRate[]>([]);
  const [showNewSupplierForm, setShowNewSupplierForm] = useState(false);
  const [newSupplierRate, setNewSupplierRate] = useState<SupplierRate>({
    supplier: '',
    supplierName: '',
    standingCharge: undefined,
    dayUnitRate: undefined,
    nightUnitRate: undefined,
    eveningWeekendRate: undefined,
    contractPeriodMonths: 12,
    annualExpenditure: 0,
    monthlyExpenditure: 0,
    percentageDifference: null,
  });
  const [quoteId, setQuoteId] = useState<string | undefined>(undefined);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [showQuoteFields, setShowQuoteFields] = useState(false);
  const [validationErrors] = useState<string[]>([]);
  const [isNonDraftEdit, setIsNonDraftEdit] = useState(false);
  const isNonDraftEditRef = useRef(false);
  const [existingQuoteStatus, setExistingQuoteStatus] = useState<string>('');
  const [existingQuoteVersion, setExistingQuoteVersion] = useState<number>(1);
  const [versionConfirmOpen, setVersionConfirmOpen] = useState(false);
  const [versionNotes, setVersionNotes] = useState('');

  const loadClient = useCallback(async (id: string) => {
    try {
      const client = await clientApi.getClient(id);
      setSelectedClient(client);
      setFormData((prev) => ({
        ...prev,
        clientId: client._id,
        propertyId: propertyIdFromUrl || prev.propertyId
      }));
    } catch (err) {
      
      toast.error('Failed to load client');
    }
  }, [propertyIdFromUrl]);

  const loadClients = async () => {
    try {
      const data = await clientApi.getClients();
      setClients(data);
    } catch (err) {
      
      toast.error('Failed to load clients');
    }
  };

  const loadProperties = async (clientId: string) => {
    try {
      const data = await propertyApi.getClientProperties(clientId);
      setProperties(data);
    } catch (err) {

      toast.error('Failed to load properties');
    }
  };

  // Audit 07 R-5: contacts for the send-target chooser follow the client.
  useEffect(() => {
    if (!formData.clientId) {
      setContacts([]);
      return;
    }
    getClientContacts(formData.clientId)
      .then(setContacts)
      .catch(() => setContacts([]));
  }, [formData.clientId]);

  const autoSaveQuote = useCallback(async () => {
    // Skip auto-save for non-draft quotes — versioning is handled by explicit save
    if (isNonDraftEditRef.current) return;
    if (!quoteId) {
      try {
        const quoteData: CreateQuoteDTO = {
          client: formData.clientId,
          property: formData.propertyId,
          meterType: formData.meterType,
          meterIdentifier: formData.meterIdentifier,
          currentSupplier: formData.currentSupplier,
          supplierRates: supplierRates,
          status: 'DRAFT',
          eac: formData.eac,
          aq: formData.aq,
          commissionUplift: formData.commissionUplift,
        };

        console.log('Auto-saving new quote with commissionUplift:', formData.commissionUplift);
        setSaveStatus('saving');
        const quote = await quoteApi.createQuote(quoteData);
        setQuoteId(quote._id);
        setSaveStatus('saved');
        console.log('Quote created with ID:', quote._id);
      } catch (err) {
        
        setSaveStatus('error');
      }
    } else {
      try {
        console.log('Auto-saving quote update with commissionUplift:', formData.commissionUplift, 'Type:', typeof formData.commissionUplift);
        const updateData = {
          client: formData.clientId,
          property: formData.propertyId,
          meterType: formData.meterType,
          meterIdentifier: formData.meterIdentifier,
          currentSupplier: formData.currentSupplier,
          supplierRates: supplierRates,
          status: 'DRAFT' as const,
          eac: formData.eac,
          aq: formData.aq,
          commissionUplift: formData.commissionUplift,
        };
        console.log('Full update data:', JSON.stringify(updateData, null, 2));
        setSaveStatus('saving');
        await quoteApi.updateDraftQuote(quoteId, updateData);
        setSaveStatus('saved');
        console.log('Quote updated successfully');
      } catch (err) {
        
        setSaveStatus('error');
      }
    }
  }, [quoteId, formData, supplierRates]);

  const autoSaveWithCurrentData = useCallback(async (currentFormData: typeof formData) => {
    // Skip auto-save for non-draft quotes — versioning is handled by explicit save
    if (isNonDraftEditRef.current) return;
    try {
      console.log('Auto-saving with current data, commissionUplift:', currentFormData.commissionUplift, 'Type:', typeof currentFormData.commissionUplift);
      const updateData = {
        client: currentFormData.clientId,
        property: currentFormData.propertyId,
        meterType: currentFormData.meterType,
        meterIdentifier: currentFormData.meterIdentifier,
        currentSupplier: currentFormData.currentSupplier,
        supplierRates: supplierRates,
        status: 'DRAFT' as const,
        eac: currentFormData.eac,
        aq: currentFormData.aq,
        commissionUplift: currentFormData.commissionUplift,
      };
      console.log('Auto-saving with data:', JSON.stringify(updateData, null, 2));
      setSaveStatus('saving');
      await quoteApi.updateDraftQuote(quoteId!, updateData);
      setSaveStatus('saved');
      console.log('Quote updated successfully');
    } catch (err) {
      
      setSaveStatus('error');
    }
  }, [quoteId, supplierRates]);

  useEffect(() => {
    if (quoteIdFromUrl) {
      // Load existing quote
      const loadQuote = async () => {
        try {
          setLoading(true);
          const quote = await quoteApi.getQuote(quoteIdFromUrl);
          console.log('Quote loaded for editing:', { 
            quoteId: quote._id, 
            commissionUplift: quote.commissionUplift,
            hasCommissionUplift: quote.commissionUplift !== undefined 
          });
          setQuoteId(quote._id);
          // Track if we're editing a non-draft quote (for versioning)
          if (quote.status !== 'DRAFT') {
            isNonDraftEditRef.current = true;
            setIsNonDraftEdit(true);
            setExistingQuoteStatus(quote.status);
            setExistingQuoteVersion(quote.version || 1);
          }
          setFormData({
            clientId: quote.client?._id || '',
            propertyId: quote.property?._id,
            meterType: quote.meterType,
            meterIdentifier: quote.meterIdentifier || '',
            eac: quote.eac,
            aq: quote.aq,
            commissionUplift: quote.commissionUplift,
            currentSupplier: quote.currentSupplier,
          });
          
          // Recalculate percentageDifference for all supplier rates to fix any legacy data issues
          // Audit 13/F-156: no meaningful baseline → null (not 0)
          const recalculatedRates = (quote.supplierRates || []).map(rate => {
            const baseline = quote.currentSupplier?.annualExpenditure;
            if (baseline != null && Number.isFinite(baseline) && baseline > 0 &&
                rate.annualExpenditure != null && Number.isFinite(rate.annualExpenditure)) {
              return {
                ...rate,
                percentageDifference: ((baseline - rate.annualExpenditure) / baseline) * 100
              };
            }
            return { ...rate, percentageDifference: null };
          });
          
          setSupplierRates(recalculatedRates);
          if (quote.client?._id) {
            loadClient(quote.client._id);
            loadProperties(quote.client._id);
          }
          // Also load all clients for the dropdown
          loadClients();
        } catch (err) {
          
          toast.error('Failed to load quote');
        } finally {
          setLoading(false);
        }
      };
      loadQuote();
    } else if (clientIdFromUrl) {
      setFormData((prev) => ({
        ...prev,
        clientId: clientIdFromUrl,
        propertyId: '',
      }));
      loadClient(clientIdFromUrl);
      loadProperties(clientIdFromUrl);
    } else {
      loadClients();
    }
  }, [quoteIdFromUrl, clientIdFromUrl, loadClient]);

  useEffect(() => {
    if (propertyIdFromUrl && clientIdFromUrl) {
      setFormData((prev) => ({
        ...prev,
        propertyId: propertyIdFromUrl
      }));
    }
  }, [propertyIdFromUrl, clientIdFromUrl]);

  useEffect(() => {
    if (formData.clientId && formData.propertyId) {
      setShowQuoteFields(true);
    } else {
      setShowQuoteFields(false);
    }
  }, [formData.clientId, formData.propertyId]);

  const scheduleAutoSave = useCallback((currentFormData: typeof formData) => {
    // Clear any existing timer using ref (synchronous, no race condition)
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set new timer
    autoSaveTimerRef.current = setTimeout(() => {
      console.log('Autosave timer fired with data:', currentFormData);
      if (!quoteId) {
        autoSaveQuote();
      } else {
        autoSaveWithCurrentData(currentFormData);
      }
    }, 1000); // 1 second debounce
  }, [quoteId, autoSaveQuote, autoSaveWithCurrentData]);

  const handleFormChange = (field: string, value: any) => {
    console.log('handleFormChange called:', field, '=', value, 'Type:', typeof value);
    
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      } as typeof formData;
      console.log('New formData after update:', field, '=', (newData as any)[field], 'Type:', typeof (newData as any)[field]);
      
      // Schedule autosave with the current data
      scheduleAutoSave(newData);
      
      return newData;
    });
  };

  // Save new version without issuing (keeps as DRAFT)
  const handleSaveVersion = async () => {
    try {
      setLoading(true);
      setError(null);

      // Client-side validation
      const validationResult = validateQuoteCreation({
        client: formData.clientId,
        property: formData.propertyId,
        meterType: formData.meterType,
        meterIdentifier: formData.meterIdentifier,
        eac: formData.eac,
        aq: formData.aq,
        currentSupplier: formData.currentSupplier
      });

      if (!validationResult.isValid) {
        const errorCount = Object.keys(validationResult.errors).length;
        const errorList = Object.entries(validationResult.errors)
          .map(([field, message]) => `• ${message}`)
          .join('\n');
        
        const errorMessage = errorCount === 1 
          ? Object.values(validationResult.errors)[0]
          : `Please fix the following ${errorCount} errors:\n${errorList}`;
        
        setError(errorMessage);
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const quoteData: CreateQuoteDTO = {
        client: formData.clientId,
        property: formData.propertyId || undefined,
        meterType: formData.meterType,
        meterIdentifier: formData.meterIdentifier,
        currentSupplier: formData.currentSupplier,
        supplierRates: supplierRates,
        status: 'DRAFT', // Keep as DRAFT
        eac: formData.eac,
        aq: formData.aq,
        commissionUplift: formData.commissionUplift,
      };

      console.log('Creating new version as DRAFT');
      const { status, ...quoteDataWithoutStatus } = quoteData;
      const savedQuote = await quoteApi.updateQuote(quoteId!, {
        ...quoteDataWithoutStatus,
        versionNotes,
        status: 'DRAFT' // Explicitly set as DRAFT
      } as any);
      setQuoteId(savedQuote._id);

      toast.success(
        `Quote v${(existingQuoteVersion + 1)} saved as draft successfully!`,
        {
          duration: 4000,
          position: 'top-right',
          icon: '✅',
        }
      );
      navigate(`/utilities/quotes/${savedQuote._id}/view`);
    } catch (err: any) {
      console.error('Quote save error:', err);
      
      let errorMessage = 'Failed to save quote version. ';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  // Save new version and issue it
  const handleSubmitQuote = async () => {
    try {
      setLoading(true);
      setError(null);

      // Client-side validation
      const validationResult = validateQuoteCreation({
        client: formData.clientId,
        property: formData.propertyId,
        meterType: formData.meterType,
        meterIdentifier: formData.meterIdentifier,
        eac: formData.eac,
        aq: formData.aq,
        currentSupplier: formData.currentSupplier
      });

      if (!validationResult.isValid) {
        // Display all validation errors
        const errorCount = Object.keys(validationResult.errors).length;
        const errorList = Object.entries(validationResult.errors)
          .map(([field, message]) => `• ${message}`)
          .join('\n');
        
        const errorMessage = errorCount === 1 
          ? Object.values(validationResult.errors)[0]
          : `Please fix the following ${errorCount} errors:\n${errorList}`;
        
        setError(errorMessage);
        setLoading(false);
        
        // Scroll to top to show error message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const quoteData: CreateQuoteDTO = {
        client: formData.clientId,
        property: formData.propertyId || undefined, // Don't send empty string
        meterType: formData.meterType,
        meterIdentifier: formData.meterIdentifier,
        currentSupplier: formData.currentSupplier,
        supplierRates: supplierRates,
        status: 'DRAFT', // Always create as DRAFT first
        eac: formData.eac,
        aq: formData.aq,
        commissionUplift: formData.commissionUplift,
      };

      let savedQuote;
      if (quoteId && isNonDraftEdit) {
        // Non-draft edit: create a new DRAFT version, then issue+send it.
        // (Audit 12: a revision is always DRAFT — the previous issued version
        // stays frozen; issuance happens only via the send operation.)
        console.log('Creating new version of non-draft quote and issuing');
        const { status, ...quoteDataWithoutStatus } = quoteData;
        savedQuote = await quoteApi.updateQuote(quoteId, {
          ...quoteDataWithoutStatus,
          versionNotes
        } as any);
        setQuoteId(savedQuote._id);

        await quoteApi.sendQuote(savedQuote._id, formData.contactId || undefined);

        toast.success(
          `Quote v${(existingQuoteVersion + 1)} created and issued successfully!`,
          {
            duration: 4000,
            position: 'top-right',
            icon: '✅',
          }
        );
        navigate(`/utilities/quotes/${savedQuote._id}/view`);
      } else if (quoteId) {
        // Draft edit: update in place then send
        console.log('Updating existing DRAFT quote');
        savedQuote = await quoteApi.updateQuote(quoteId, quoteData);
        setQuoteId(savedQuote._id);

        // Now send the quote (which will change status to ISSUED and send email with PDF)
        await quoteApi.sendQuote(savedQuote._id, formData.contactId || undefined);

        toast.success(
          `Quote for ${selectedClient?.companyName || 'client'} issued and sent successfully!`,
          {
            duration: 4000,
            position: 'top-right',
            icon: '✅',
          }
        );
        navigate('/utilities/quotes');
      } else {
        // New quote: create then send
        savedQuote = await quoteApi.createQuote({
          client: formData.clientId,
          property: formData.propertyId || undefined,
          meterType: formData.meterType,
          meterIdentifier: formData.meterIdentifier,
          currentSupplier: formData.currentSupplier,
          supplierRates: supplierRates,
          status: 'DRAFT',
          dateOfQuotation: new Date().toISOString(),
          eac: formData.eac,
          aq: formData.aq,
          commissionUplift: formData.commissionUplift
        });
        setQuoteId(savedQuote._id);

        // Now send the quote (which will change status to ISSUED and send email with PDF)
        await quoteApi.sendQuote(savedQuote._id, formData.contactId || undefined);

        toast.success(
          `Quote for ${selectedClient?.companyName || 'client'} issued and sent successfully!`,
          {
            duration: 4000,
            position: 'top-right',
            icon: '✅',
          }
        );
        navigate('/utilities/quotes');
      }
    } catch (err: any) {
      console.error('Quote submission error:', err);
      
      // Build user-friendly error message
      let errorMessage = 'Failed to save quote. ';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.errors) {
        const fieldErrors = err.response.data.errors;
        const errorList = Object.entries(fieldErrors)
          .map(([field, msg]) => `• ${field}: ${msg}`)
          .join('\n');
        errorMessage = `Please fix the following errors:\n${errorList}`;
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid quote data. Please check all required fields and try again.';
      } else if (err.response?.status === 409) {
        errorMessage = 'A quote with this information already exists.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error occurred. Please try again or contact support if the problem persists.';
      } else if (err.message) {
        errorMessage = err.message;
      } else {
        errorMessage = 'An unexpected error occurred. Please try again.';
      }
      
      setError(errorMessage);
      
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const handlePropertyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const propertyId = event.target.value as string;
    setFormData((prev) => ({
      ...prev,
      propertyId: propertyId
    }));
  };

  useEffect(() => {
    return () => {
      // Clean up timer on unmount
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Auto-save when supplier rates change (draft quotes only)
  useEffect(() => {
    if (quoteId && supplierRates.length > 0 && !isNonDraftEditRef.current) {
      console.log('Supplier rates changed, triggering auto-save');
      scheduleAutoSave(formData);
    }
  }, [supplierRates, quoteId, formData, scheduleAutoSave]);

  const handleCurrentSupplierChange = (
    field: keyof FormData['currentSupplier'],
    value: any
  ) => {
    const updatedSupplier = {
      ...formData.currentSupplier,
      [field]: value,
    };

    // Calculate annual and monthly expenditure
    const annualExpenditure = calculateAnnualExpenditure(
      updatedSupplier.standingCharge || 0,
      updatedSupplier.dayUnitRate || 0,
      updatedSupplier.nightUnitRate || 0,
      updatedSupplier.eveningWeekendRate || 0,
      updatedSupplier.annualConsumption.day,
      updatedSupplier.annualConsumption.night || 0,
      updatedSupplier.annualConsumption.eveningWeekend || 0
    );

    const monthlyExpenditure = annualExpenditure / 12;

    const updatedData = {
      ...formData,
      currentSupplier: {
        ...updatedSupplier,
        annualExpenditure,
        monthlyExpenditure,
      },
    };

    setFormData(updatedData);
    handleFormChange('currentSupplier', updatedData.currentSupplier);
  };

  const handleAddSupplierRate = () => {
    if (
      !newSupplierRate.supplier ||
      !newSupplierRate.contractPeriodMonths
    ) {
      toast.error('Please fill in all required supplier rate fields');
      return;
    }

    const annualExpenditure = calculateAnnualExpenditure(
      newSupplierRate.standingCharge || 0,
      newSupplierRate.dayUnitRate || 0,
      newSupplierRate.nightUnitRate || 0,
      newSupplierRate.eveningWeekendRate || 0,
      formData.currentSupplier.annualConsumption.day,
      formData.currentSupplier.annualConsumption.night || 0,
      formData.currentSupplier.annualConsumption.eveningWeekend || 0
    );

    const monthlyExpenditure = annualExpenditure / 12;
    const currentAnnualExpenditure = calculateAnnualExpenditure(
      formData.currentSupplier.standingCharge || 0,
      formData.currentSupplier.dayUnitRate || 0,
      formData.currentSupplier.nightUnitRate || 0,
      formData.currentSupplier.eveningWeekendRate || 0,
      formData.currentSupplier.annualConsumption.day,
      formData.currentSupplier.annualConsumption.night || 0,
      formData.currentSupplier.annualConsumption.eveningWeekend || 0
    );

    // Audit 13/F-156: null = no meaningful baseline (matches server semantics)
    const percentageDifference =
      currentAnnualExpenditure > 0 && Number.isFinite(annualExpenditure)
        ? ((currentAnnualExpenditure - annualExpenditure) / currentAnnualExpenditure) * 100
        : null;

    const newRate: SupplierRate = {
      ...newSupplierRate as SupplierRate,
      annualExpenditure,
      monthlyExpenditure,
      percentageDifference,
    };

    setSupplierRates((prev) => [...prev, newRate]);
    setNewSupplierRate({
      supplier: '',
      supplierName: '',
      standingCharge: undefined,
      dayUnitRate: undefined,
      nightUnitRate: undefined,
      eveningWeekendRate: undefined,
      contractPeriodMonths: 12,
      annualExpenditure: 0,
      monthlyExpenditure: 0,
      percentageDifference: null,
    });
    setShowNewSupplierForm(false);
    toast.success('New supplier rate added');
  };

  const handleUpdateExistingSupplierRate = (index: number, field: keyof SupplierRate, value: any) => {
    setSupplierRates((prev) => {
      const updated = [...prev];
      const rate = { ...updated[index], [field]: value };

      // Recalculate expenditure and savings when rate fields change
      const recalcFields: (keyof SupplierRate)[] = ['standingCharge', 'dayUnitRate', 'nightUnitRate', 'eveningWeekendRate'];
      if (recalcFields.includes(field)) {
        const annualExpenditure = calculateAnnualExpenditure(
          rate.standingCharge || 0,
          rate.dayUnitRate || 0,
          rate.nightUnitRate || 0,
          rate.eveningWeekendRate || 0,
          formData.currentSupplier.annualConsumption.day,
          formData.currentSupplier.annualConsumption.night || 0,
          formData.currentSupplier.annualConsumption.eveningWeekend || 0
        );
        rate.annualExpenditure = annualExpenditure;
        rate.monthlyExpenditure = annualExpenditure / 12;

        const currentAnnualExpenditure = calculateAnnualExpenditure(
          formData.currentSupplier.standingCharge || 0,
          formData.currentSupplier.dayUnitRate || 0,
          formData.currentSupplier.nightUnitRate || 0,
          formData.currentSupplier.eveningWeekendRate || 0,
          formData.currentSupplier.annualConsumption.day,
          formData.currentSupplier.annualConsumption.night || 0,
          formData.currentSupplier.annualConsumption.eveningWeekend || 0
        );
        rate.percentageDifference = currentAnnualExpenditure > 0
          ? ((currentAnnualExpenditure - annualExpenditure) / currentAnnualExpenditure) * 100
          : null;
      }

      updated[index] = rate;
      return updated;
    });
  };

  const handleDeleteSupplierRate = (index: number) => {
    setSupplierRates((prev) => prev.filter((rate, i) => i !== index));
  };

  const handleSupplierRateChange = (
    field: keyof SupplierRate,
    value: number | string
  ) => {
    const updatedRate = {
      ...newSupplierRate,
      [field]: value,
    };
    setNewSupplierRate(updatedRate);

    if (
      field === 'standingCharge' ||
      field === 'dayUnitRate' ||
      field === 'nightUnitRate' ||
      field === 'eveningWeekendRate'
    ) {
      const annualExpenditure = calculateAnnualExpenditure(
        updatedRate.standingCharge || 0,
        updatedRate.dayUnitRate || 0,
        updatedRate.nightUnitRate || 0,
        updatedRate.eveningWeekendRate || 0,
        formData.currentSupplier.annualConsumption.day,
        formData.currentSupplier.annualConsumption.night || 0,
        formData.currentSupplier.annualConsumption.eveningWeekend || 0
      );

      const monthlyExpenditure = annualExpenditure / 12;
      const currentAnnualExpenditure = calculateAnnualExpenditure(
        formData.currentSupplier.standingCharge || 0,
        formData.currentSupplier.dayUnitRate || 0,
        formData.currentSupplier.nightUnitRate || 0,
        formData.currentSupplier.eveningWeekendRate || 0,
        formData.currentSupplier.annualConsumption.day,
        formData.currentSupplier.annualConsumption.night || 0,
        formData.currentSupplier.annualConsumption.eveningWeekend || 0
      );

      const percentageDifference =
        ((currentAnnualExpenditure - annualExpenditure) / currentAnnualExpenditure) * 100;

      setNewSupplierRate((prev) => ({
        ...prev,
        annualExpenditure,
        monthlyExpenditure,
        percentageDifference,
      }));
    }
  };

  const calculateAnnualExpenditure = (
    standingCharge: number,
    dayRate: number,
    nightRate: number,
    eveningWeekendRate: number,
    dayConsumption: number,
    nightConsumption: number,
    eveningWeekendConsumption: number
  ) => {
    // Standing charge is in pence per day
    const annualStandingCharge = standingCharge * 365;

    // Rates are in pence per kWh
    const dayCharge = dayRate * dayConsumption;
    const nightCharge = nightRate * nightConsumption;
    const eveningWeekendCharge = eveningWeekendRate * eveningWeekendConsumption;

    // Total annual cost in pence, then convert to pounds
    return (annualStandingCharge + dayCharge + nightCharge + eveningWeekendCharge) / 100;
  };

  const formatAddress = (address: PropertyAddress) => {
    const parts = [
      address.line1,
      address.line2,
      address.city,
      address.county,
      address.postcode,
    ].filter(Boolean);
    return parts.join(', ');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" component="h1">
            {quoteId ? 'Edit Quote' : 'Create Quote'}
          </Typography>
          <Box>
            {saveStatus === 'saving' && (
              <Typography color="text.secondary" sx={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem' }}>
                <CircularProgress size={16} sx={{ mr: 1 }} /> Saving...
              </Typography>
            )}
            {saveStatus === 'saved' && (
              <Typography color="success.main" sx={{ fontSize: '0.875rem' }}>✓ All changes saved</Typography>
            )}
            {saveStatus === 'error' && (
              <Typography color="error" sx={{ fontSize: '0.875rem' }}>⚠ Auto-save failed</Typography>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography sx={{ whiteSpace: 'pre-line' }}>{error}</Typography>
          </Alert>
        )}

        {isNonDraftEdit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography>
              You are editing an <strong>{existingQuoteStatus}</strong> quote (v{existingQuoteVersion}). 
              Saving will create <strong>v{existingQuoteVersion + 1}</strong> while preserving the original version in the history.
            </Typography>
          </Alert>
        )}

        <form onSubmit={(e) => {
          e.preventDefault();
          handleSubmitQuote();
        }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={clients.sort((a, b) => {
                  const nameA = a.companyName || a.ownerName || '';
                  const nameB = b.companyName || b.ownerName || '';
                  return nameA.localeCompare(nameB);
                })}
                getOptionLabel={(option) => option.companyName || option.ownerName || ''}
                value={selectedClient}
                onChange={(event, newValue) => {
                  setSelectedClient(newValue);
                  if (newValue) {
                    setFormData((prev) => ({
                      ...prev,
                      clientId: newValue._id,
                      propertyId: '',
                      contactId: ''
                    }));
                    loadProperties(newValue._id);
                  } else {
                    setFormData((prev) => ({
                      ...prev,
                      clientId: '',
                      propertyId: '',
                      contactId: ''
                    }));
                    setProperties([]);
                    setContacts([]);
                  }
                }}
                disabled={!!clientIdFromUrl}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    error={validationErrors.includes('Client is required')}
                    helperText={
                      validationErrors.includes('Client is required')
                        ? 'Client is required'
                        : ''
                    }
                  />
                )}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: 'rgba(30, 41, 59, 0.9)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(148, 163, 184, 0.1)',
                      borderRadius: '8px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      '& .MuiAutocomplete-option': {
                        color: '#e2e8f0',
                        '&:hover': {
                          backgroundColor: 'rgba(51, 65, 85, 0.8)',
                        },
                        '&[aria-selected="true"]': {
                          backgroundColor: 'rgba(59, 130, 246, 0.3)',
                          '&:hover': {
                            backgroundColor: 'rgba(59, 130, 246, 0.4)',
                          },
                        },
                      },
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {formData.clientId && (
                <TextField
                  select
                  fullWidth
                  label="Property"
                  value={formData.propertyId}
                  onChange={handlePropertyChange}
                  disabled={false}
                  error={validationErrors.includes('Property is required')}
                  helperText={
                    validationErrors.includes('Property is required')
                      ? 'Property is required'
                      : ''
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {properties.map((property) => (
                    <MenuItem key={property._id} value={property._id}>
                      {property.name} - {formatAddress(property.address)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              {formData.clientId && contacts.length > 0 && (
                <TextField
                  select
                  fullWidth
                  label="Send to contact"
                  value={formData.contactId || ''}
                  onChange={(e) => handleFormChange('contactId', e.target.value)}
                  helperText="Who this quote is addressed to — defaults to the primary contact"
                >
                  <MenuItem value="">
                    <em>Default — primary contact</em>
                  </MenuItem>
                  {contacts.map((contact) => (
                    <MenuItem key={contact._id} value={contact._id}>
                      {contact.name}
                      {contact.email ? ` — ${contact.email}` : ''}
                      {contact.roles?.length ? ` [${contact.roles.join(', ')}]` : ''}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>

            {showQuoteFields && (
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Meter Type"
                      value={formData.meterType}
                      onChange={(e) =>
                        handleFormChange('meterType', e.target.value as 'MPAN' | 'MPRN')
                      }
                      required
                    >
                      <MenuItem value="MPAN">Electricity</MenuItem>
                      <MenuItem value="MPRN">Gas</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={formData.meterType === 'MPAN' ? 'MPAN (Meter Point Administration Number)' : 'MPRN (Meter Point Reference Number)'}
                      value={formData.meterIdentifier}
                      onChange={(e) => handleFormChange('meterIdentifier', e.target.value)}
                      required
                      error={validationErrors.includes('Meter identifier is required')}
                      helperText={
                        validationErrors.includes('Meter identifier is required')
                          ? 'Meter identifier is required'
                          : ''
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Current Supplier Details
                    </Typography>
                  </Grid>

                  {/* Supplier Dropdown - Half Width */}
                  <Grid item xs={12} md={6}>
                    <SupplierDropdown
                      value={formData.currentSupplier.supplier}
                      onChange={(supplierId, supplierName) => {
                        setFormData(prev => ({
                          ...prev,
                          currentSupplier: {
                            ...prev.currentSupplier,
                            supplier: supplierId,
                            supplierName: supplierName
                          }
                        }));
                      }}
                      label="Current Supplier"
                      required
                      meterType={formData.meterType}
                      error={validationErrors.includes('Current supplier is required')}
                      helperText={
                        validationErrors.includes('Current supplier is required')
                          ? 'Current supplier is required'
                          : ''
                      }
                    />
                  </Grid>

                  {/* EAC/AQ Field - Half Width to balance with Current Supplier */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label={formData.meterType === 'MPAN' ? 'EAC - Estimated Annual Consumption (kWh)' : 'AQ - Annual Quantity (kWh)'}
                      value={formData.meterType === 'MPAN' ? formData.eac || '' : formData.aq || ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || undefined;
                        if (formData.meterType === 'MPAN') {
                          handleFormChange('eac', value);
                        } else {
                          handleFormChange('aq', value);
                        }
                      }}
                      required
                      helperText={formData.meterType === 'MPAN' ? 'Annual electricity consumption in kWh' : 'Annual gas consumption in kWh'}
                    />
                  </Grid>

                  {/* Commission Uplift Field - Internal Only */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Commission Uplift (Internal)"
                      value={formData.commissionUplift ?? ''}
                      onChange={(e) => {
                        const value = e.target.value.trim();
                        console.log('Commission uplift input changed:', value);
                        if (value === '') {
                          console.log('Setting commissionUplift to undefined');
                          handleFormChange('commissionUplift', undefined);
                        } else {
                          const numValue = parseFloat(value);
                          console.log('Parsed value:', numValue, 'Type:', typeof numValue);
                          if (!isNaN(numValue) && numValue >= 0 && numValue <= 5.0) {
                            // Store exact value without rounding
                            console.log('Calling handleFormChange with:', numValue);
                            handleFormChange('commissionUplift', numValue);
                          }
                        }
                      }}
                      onBlur={() => {
                        // Save immediately on blur to guarantee final value
                        if (formData.commissionUplift !== undefined) {
                          scheduleAutoSave(formData);
                        }
                      }}
                      inputProps={{
                        min: 0,
                        max: 5.0,
                        step: 0.1
                      }}
                      helperText="Internal field only (0.0 - 5.0). Not shown in quote PDF."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 193, 7, 0.05)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 193, 7, 0.1)',
                          }
                        }
                      }}
                    />
                  </Grid>

                  {/* Rates Section - Organized in a cleaner grid layout */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Rates & Consumption
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Standing Charge (p/day)"
                      value={formData.currentSupplier.standingCharge ?? ''}
                      onChange={(e) => {
                        const value = e.target.value.trim();
                        handleCurrentSupplierChange('standingCharge', value === '' ? undefined : parseFloat(value));
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Day Unit Rate (p/kWh)"
                      value={formData.currentSupplier.dayUnitRate ?? ''}
                      onChange={(e) => {
                        const value = e.target.value.trim();
                        handleCurrentSupplierChange('dayUnitRate', value === '' ? undefined : parseFloat(value));
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Night Unit Rate (p/kWh)"
                      value={formData.currentSupplier.nightUnitRate ?? ''}
                      onChange={(e) => {
                        const value = e.target.value.trim();
                        handleCurrentSupplierChange('nightUnitRate', value === '' ? undefined : parseFloat(value));
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Evening & Weekend Rate (p/kWh)"
                      value={formData.currentSupplier.eveningWeekendRate ?? ''}
                      onChange={(e) => {
                        const value = e.target.value.trim();
                        handleCurrentSupplierChange('eveningWeekendRate', value === '' ? undefined : parseFloat(value));
                      }}
                    />
                  </Grid>

                  {/* Consumption Section - Organized in a cleaner grid layout */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Day Consumption (kWh)"
                      value={formData.currentSupplier.annualConsumption.day}
                      onChange={(e) =>
                        handleCurrentSupplierChange('annualConsumption', {
                          ...formData.currentSupplier.annualConsumption,
                          day: parseFloat(e.target.value),
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Night Consumption (kWh)"
                      value={formData.currentSupplier.annualConsumption.night || ''}
                      onChange={(e) =>
                        handleCurrentSupplierChange('annualConsumption', {
                          ...formData.currentSupplier.annualConsumption,
                          night: parseFloat(e.target.value),
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Evening & Weekend Consumption (kWh)"
                      value={formData.currentSupplier.annualConsumption.eveningWeekend || ''}
                      onChange={(e) =>
                        handleCurrentSupplierChange('annualConsumption', {
                          ...formData.currentSupplier.annualConsumption,
                          eveningWeekend: parseFloat(e.target.value),
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6">Supplier Rates</Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setShowNewSupplierForm(true)}
                        startIcon={<AddIcon />}
                      >
                        Add Supplier
                      </Button>
                    </Box>

                    {/* New Supplier Form */}
                    {showNewSupplierForm && (
                      <Paper sx={{ p: 2, mb: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <SupplierDropdown
                              value={newSupplierRate.supplier}
                              onChange={(supplierId, supplierName) => {
                                setNewSupplierRate(prev => ({
                                  ...prev,
                                  supplier: supplierId,
                                  supplierName: supplierName
                                }));
                              }}
                              label="Supplier"
                              required
                              meterType={formData.meterType}
                            />
                            <TextField
                              fullWidth
                              label="Standing Charge (p/day)"
                              type="number"
                              value={newSupplierRate.standingCharge ?? ''}
                              onChange={(e) => {
                                const value = e.target.value.trim();
                                if (value === '') {
                                  handleSupplierRateChange('standingCharge', '');
                                } else {
                                  const parsed = parseFloat(value);
                                  handleSupplierRateChange('standingCharge', isNaN(parsed) ? 0 : parsed);
                                }
                              }}
                              sx={{ mt: 2 }}
                            />
                            <TextField
                              fullWidth
                              label="Day Rate (p/kWh)"
                              type="number"
                              value={newSupplierRate.dayUnitRate ?? ''}
                              onChange={(e) => {
                                const value = e.target.value.trim();
                                if (value === '') {
                                  handleSupplierRateChange('dayUnitRate', '');
                                } else {
                                  const parsed = parseFloat(value);
                                  handleSupplierRateChange('dayUnitRate', isNaN(parsed) ? 0 : parsed);
                                }
                              }}
                              sx={{ mt: 2 }}
                            />
                            {formData.currentSupplier?.nightUnitRate !== undefined && (
                              <TextField
                                fullWidth
                                label="Night Rate (p/kWh)"
                                type="number"
                                value={newSupplierRate.nightUnitRate ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value.trim();
                                  if (value === '') {
                                    handleSupplierRateChange('nightUnitRate', '');
                                  } else {
                                    const parsed = parseFloat(value);
                                    handleSupplierRateChange('nightUnitRate', isNaN(parsed) ? 0 : parsed);
                                  }
                                }}
                                sx={{ mt: 2 }}
                              />
                            )}
                            {formData.currentSupplier?.eveningWeekendRate !== undefined && (
                              <TextField
                                fullWidth
                                label="Evening/Weekend Rate (p/kWh)"
                                type="number"
                                value={newSupplierRate.eveningWeekendRate ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value.trim();
                                  if (value === '') {
                                    handleSupplierRateChange('eveningWeekendRate', '');
                                  } else {
                                    const parsed = parseFloat(value);
                                    handleSupplierRateChange('eveningWeekendRate', isNaN(parsed) ? 0 : parsed);
                                  }
                                }}
                                sx={{ mt: 2 }}
                              />
                            )}
                            <TextField
                              fullWidth
                              label="Contract Period (months)"
                              type="number"
                              value={newSupplierRate.contractPeriodMonths}
                              onChange={(e) => {
                                const value = e.target.value.trim();
                                handleSupplierRateChange('contractPeriodMonths', value === '' ? '' : parseInt(value) || 0);
                              }}
                              required
                              sx={{ mt: 2 }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Annual Cost (£)"
                              value={(newSupplierRate.annualExpenditure || 0).toFixed(2)}
                              disabled
                            />
                            <TextField
                              fullWidth
                              label="Monthly Cost (£)"
                              value={(newSupplierRate.monthlyExpenditure || 0).toFixed(2)}
                              disabled
                              sx={{ mt: 2 }}
                            />
                            <TextField
                              fullWidth
                              label="Savings (%)"
                              value={newSupplierRate.percentageDifference != null ? newSupplierRate.percentageDifference.toFixed(2) : 'N/A'}
                              disabled
                              sx={{ mt: 2 }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box
                              display="flex"
                              justifyContent="flex-end"
                              gap={2}
                              sx={{ mt: 2 }}
                            >
                              <Button
                                variant="outlined"
                                onClick={() => setShowNewSupplierForm(false)}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="contained"
                                onClick={handleAddSupplierRate}
                                disabled={
                                  !newSupplierRate.supplier
                                }
                              >
                                Add
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    )}

                    {/* Supplier Rates List */}
                    {supplierRates.map((rate, index) => (
                      <Paper key={index} sx={{ p: 2, mb: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Supplier"
                              value={rate.supplierName}
                              disabled
                            />
                            <TextField
                              fullWidth
                              label="Standing Charge (p/day)"
                              type="number"
                              value={rate.standingCharge ?? ''}
                              onChange={(e) => {
                                const val = e.target.value.trim();
                                handleUpdateExistingSupplierRate(index, 'standingCharge', val === '' ? undefined : parseFloat(val));
                              }}
                              sx={{ mt: 2 }}
                            />
                            <TextField
                              fullWidth
                              label="Day Unit Rate (p/kWh)"
                              type="number"
                              value={rate.dayUnitRate ?? ''}
                              onChange={(e) => {
                                const val = e.target.value.trim();
                                handleUpdateExistingSupplierRate(index, 'dayUnitRate', val === '' ? undefined : parseFloat(val));
                              }}
                              sx={{ mt: 2 }}
                            />
                            {rate.nightUnitRate !== undefined && (
                              <TextField
                                fullWidth
                                label="Night Unit Rate (p/kWh)"
                                type="number"
                                value={rate.nightUnitRate ?? ''}
                                onChange={(e) => {
                                  const val = e.target.value.trim();
                                  handleUpdateExistingSupplierRate(index, 'nightUnitRate', val === '' ? undefined : parseFloat(val));
                                }}
                                sx={{ mt: 2 }}
                              />
                            )}
                            {rate.eveningWeekendRate !== undefined && (
                              <TextField
                                fullWidth
                                label="Evening & Weekend Rate (p/kWh)"
                                type="number"
                                value={rate.eveningWeekendRate ?? ''}
                                onChange={(e) => {
                                  const val = e.target.value.trim();
                                  handleUpdateExistingSupplierRate(index, 'eveningWeekendRate', val === '' ? undefined : parseFloat(val));
                                }}
                                sx={{ mt: 2 }}
                              />
                            )}
                            <TextField
                              fullWidth
                              label="Contract Period (months)"
                              type="number"
                              value={rate.contractPeriodMonths}
                              onChange={(e) => {
                                const val = e.target.value.trim();
                                handleUpdateExistingSupplierRate(index, 'contractPeriodMonths', val === '' ? '' : parseInt(val) || 0);
                              }}
                              sx={{ mt: 2 }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Annual Cost (£)"
                              value={(rate.annualExpenditure || 0).toFixed(2)}
                              disabled
                            />
                            <TextField
                              fullWidth
                              label="Monthly Cost (£)"
                              value={(rate.monthlyExpenditure || 0).toFixed(2)}
                              disabled
                              sx={{ mt: 2 }}
                            />
                            <TextField
                              fullWidth
                              label="Savings (%)"
                              value={rate.percentageDifference != null ? rate.percentageDifference.toFixed(2) : 'N/A'}
                              disabled
                              sx={{ mt: 2 }}
                            />
                            <Box
                              display="flex"
                              justifyContent="flex-end"
                              mt={2}
                              sx={{ mb: 2 }}
                            >
                              <Button
                                variant="outlined"
                                color="error"
                                onClick={() => handleDeleteSupplierRate(index)}
                                startIcon={<DeleteIcon />}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Continue button removed - form automatically shows when client and property are selected */}
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {isNonDraftEdit ? 'Editing will create a new version' : 'Changes are automatically saved'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                >
                  Close
                </Button>
                {isNonDraftEdit && (
                  <>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setVersionConfirmOpen(true);
                      }}
                      disabled={loading}
                    >
                      Save as v{existingQuoteVersion + 1}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setVersionConfirmOpen(true)}
                      disabled={loading}
                    >
                      Save as v{existingQuoteVersion + 1} & Issue
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        </form>
      </Paper>

      {/* Version Confirmation Dialog */}
      <Dialog
        open={versionConfirmOpen}
        onClose={() => setVersionConfirmOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            color: 'white',
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>Create New Version</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: 'rgba(255, 255, 255, 0.8)' }}>
            This will create <Chip label={`v${existingQuoteVersion + 1}`} size="small" color="primary" sx={{ mx: 0.5 }} /> of quote while preserving v{existingQuoteVersion} in the version history.
          </Typography>
          <TextField
            fullWidth
            label="Version Notes (optional)"
            placeholder="e.g. Updated supplier rates, changed contract period..."
            multiline
            rows={2}
            value={versionNotes}
            onChange={(e) => setVersionNotes(e.target.value)}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.5)' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setVersionConfirmOpen(false)}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              setVersionConfirmOpen(false);
              handleSaveVersion();
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : `Save as v${existingQuoteVersion + 1}`}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setVersionConfirmOpen(false);
              handleSubmitQuote();
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : `Save as v${existingQuoteVersion + 1} & Issue`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuoteForm;
